/**
 * StoreKit 2 bridge for native iOS.
 *
 * The native side is implemented by the `MBLearnIap` Capacitor plugin
 * (ios/App/App/MBLearnIapPlugin.swift). On web — and on any platform where
 * the plugin is not installed — every method rejects, so callers must always
 * handle failure. No entitlement is ever granted from this layer: signed
 * transactions are forwarded to the `validate-apple-receipt` edge function,
 * which is the only source of truth.
 */
import { Capacitor, registerPlugin, type PluginListenerHandle } from "@capacitor/core";

export interface AppleProduct {
  productId: string;
  displayName: string;
  description: string;
  /** Localized price string from StoreKit, e.g. "$14.99" — never hardcoded. */
  displayPrice: string;
  price: number;
  currencyCode: string;
  /** e.g. "P1M", "P3M", "P1Y" */
  subscriptionPeriod: string | null;
  /** Localized introductory offer price, when Apple configures one. */
  introductoryOffer: {
    displayPrice: string;
    period: string;
    paymentMode: string;
  } | null;
}

export type PurchaseOutcome = "success" | "pending" | "cancelled" | "failed";

export interface PurchaseResult {
  result: PurchaseOutcome;
  signedTransaction?: string;
  message?: string;
}

export interface TransactionsResult {
  signedTransactions: string[];
}

export interface MBLearnIapPlugin {
  isAvailable(): Promise<{ available: boolean; plugin?: string }>;
  getProducts(options: { productIds: string[] }): Promise<{
    products: AppleProduct[];
    diagnostic?: {
      bundleId?: string;
      storefront?: string;
      environment?: string;
      /** "sandboxReceipt" | "receipt" | "none" — environment hint, no contents. */
      receiptKind?: string;
      requestedProductIds?: string[];
      /** Byte-level problems detected natively (whitespace, casing, prefix). */
      idIssues?: string[];
      /** Ids Apple did not return even though no error was thrown. */
      missingProductIds?: string[];
      productCount?: number;
      returnedProductIds?: string[];
    };
  }>;

  purchase(options: {
    productId: string;
    appAccountToken?: string;
  }): Promise<PurchaseResult>;
  restorePurchases(): Promise<TransactionsResult>;
  getCurrentEntitlements(): Promise<TransactionsResult>;
  showManageSubscriptions(): Promise<void>;
  addListener(
    eventName: "transactionUpdated",
    listener: (event: { signedTransaction: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const unavailable = <T,>(): Promise<T> =>
  Promise.reject(new Error("storekit_unavailable"));

export const MBLearnIap = registerPlugin<MBLearnIapPlugin>("MBLearnIap", {
  web: () => ({
    isAvailable: () => Promise.resolve({ available: false }),
    getProducts: () => unavailable(),
    purchase: () => unavailable(),
    restorePurchases: () => unavailable(),
    getCurrentEntitlements: () => unavailable(),
    showManageSubscriptions: () => unavailable(),
    addListener: () =>
      Promise.resolve({ remove: () => Promise.resolve() } as PluginListenerHandle),
  }),
});

/** True inside the native iOS shell (independent of plugin registration timing). */
export function isNativeIosShell(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
  } catch {
    return false;
  }
}

/** Exact synchronous value reported by the Capacitor runtime. */
export function isCapacitorIapPluginAvailable(): boolean {
  try {
    return Capacitor.isPluginAvailable("MBLearnIap");
  } catch {
    return false;
  }
}

/** Synchronous hint only — may be false before the bridge finishes mounting. */
export function isStoreKitPlatform(): boolean {
  try {
    return isNativeIosShell() && Capacitor.isPluginAvailable("MBLearnIap");
  } catch {
    return false;
  }
}

/**
 * Authoritative availability probe: asks the native plugin directly instead of
 * trusting `isPluginAvailable`, which can be false during early boot.
 * Retries a few times while the bridge finishes registering plugins.
 */
export async function probeStoreKitAvailability(
  attempts = 5,
  delayMs = 400,
): Promise<{ available: boolean; detail: string }> {
  if (!isNativeIosShell()) return { available: false, detail: "not_native_ios" };

  let lastDetail = "unknown";
  for (let i = 0; i < attempts; i += 1) {
    const registered = isCapacitorIapPluginAvailable();
    console.info("🍎 [IAP] Capacitor.isPluginAvailable", {
      attempt: i + 1,
      plugin: "MBLearnIap",
      registered,
    });
    try {
      const res = await MBLearnIap.isAvailable();
      console.info("🍎 [IAP] native isAvailable result", res);
      if (res?.available) {
        return { available: true, detail: `available_attempt_${i + 1}` };
      }
      lastDetail = "plugin_reported_unavailable";
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      lastDetail = registered ? `native_probe_failed: ${message}` : `plugin_not_registered: ${message}`;
      console.error("🍎 [IAP] native isAvailable FAILED", {
        attempt: i + 1,
        registered,
        detail: lastDetail,
        error: e,
      });
    }
    if (i < attempts - 1) {
      // eslint-disable-next-line no-await-in-loop -- intentional backoff
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return { available: false, detail: lastDetail };
}
